import { Command } from 'commander';
import { readFileSync } from 'node:fs';
import { ApiClient } from '../client.js';
import { formatOutput } from '../output.js';
import { getBase, getFormat } from '../helpers.js';
import { confirm } from '../prompt.js';

export const attachmentsCommand = new Command('attachments')
  .description('Manage document attachments');

attachmentsCommand.command('upload <record_type> <id>')
  .description('Get a presigned upload URL for a document attachment. With --file, uploads the file content in one step.')
  .requiredOption('--filename <name>', 'File name or path (e.g. docs/guide.md)')
  .option('--content-type <type>', 'MIME type', 'application/octet-stream')
  .option('--file <path>', 'Local file to upload (skips presigned URL output, uploads directly)')
  .action(async function (this: Command, record_type: string, id: string, opts: { filename: string; contentType: string; file?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const data = await client.request('POST', `/v1/${ws}/documents/${record_type}/${id}/attachments`, {
      filename: opts.filename,
      content_type: opts.contentType,
    }) as { upload_url: string; [key: string]: unknown };

    if (opts.file) {
      const body = readFileSync(opts.file);
      const uploadUrl = data.upload_url.startsWith('http') ? data.upload_url : `${client.baseUrl}${data.upload_url}`;
      await client.uploadFile(uploadUrl, body, opts.contentType);
      console.log(formatOutput({ ...data, uploaded: true }, getFormat(this)));
    } else {
      console.log(formatOutput(data, getFormat(this)));
    }
  });

attachmentsCommand.command('url <record_type> <id>')
  .description('Get a download URL for an attachment by filename')
  .requiredOption('--filename <name>', 'File name or path (e.g. docs/guide.md)')
  .action(async function (this: Command, record_type: string, id: string, opts: { filename: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const attachments = await client.request<Array<{ key: string; size: number; uploaded: string }>>('GET', `/v1/${ws}/documents/${record_type}/${id}/attachments`);
    const match = attachments.find(a => a.key.endsWith(`/${opts.filename}`));
    if (!match) {
      throw new Error(`Attachment "${opts.filename}" not found`);
    }
    const downloadUrl = `${client.baseUrl}/v1/${ws}/attachments/${match.key}`;
    console.log(formatOutput({ download_url: downloadUrl, ...match }, getFormat(this)));
  });

attachmentsCommand.command('list <record_type> <id>')
  .description('List attachments for a document')
  .option('--prefix <path>', 'Filter by path prefix (e.g. docs/)')
  .action(async function (this: Command, record_type: string, id: string, opts: { prefix?: string }) {
    const ws = getBase(this);
    const client = new ApiClient();
    const query = opts.prefix ? `?prefix=${encodeURIComponent(opts.prefix)}` : '';
    const data = await client.request('GET', `/v1/${ws}/documents/${record_type}/${id}/attachments${query}`);
    console.log(formatOutput(data, getFormat(this)));
  });

attachmentsCommand.command('delete <record_type> <id> <attachment-id>')
  .description('Delete an attachment')
  .option('-y, --yes', 'Skip confirmation prompt')
  .action(async function (this: Command, record_type: string, id: string, attachmentId: string, opts: { yes?: boolean }) {
    if (!opts.yes && !(await confirm(`Delete attachment ${attachmentId} on ${record_type}/${id}? This cannot be undone.`))) {
      console.log('Aborted');
      return;
    }
    const ws = getBase(this);
    const client = new ApiClient();
    await client.request('DELETE', `/v1/${ws}/documents/${record_type}/${id}/attachments/${attachmentId}`);
    console.log('Deleted');
  });
